import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
  Alert,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useApi } from "../../utils/api";
import VideoPlayerModal from "../Shared/VideoPlayerModal";
import {
  fetchYouTubeVideoTitle,
  getYouTubeDisplayLabel,
  getYouTubeThumbnailUrl,
  normalizeYouTubeVideo,
  parseYouTubeVideoId,
} from "../../utils/youtubeVideo";

function VideoSelectionModal({ open, onClose, onSelectVideo }) {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [tab, setTab] = useState(0);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeError, setYoutubeError] = useState("");
  const [youtubeTitle, setYoutubeTitle] = useState("");
  const { getContentMetadata } = useApi();

  const [playerOpen, setPlayerOpen] = useState(false);
  const [playingVideoKey, setPlayingVideoKey] = useState(null);
  const [playingVideoName, setPlayingVideoName] = useState(null);

  const parsedYoutubeId = parseYouTubeVideoId(youtubeUrl);
  const normalizedYoutubeValue = parsedYoutubeId
    ? `youtube:${parsedYoutubeId}`
    : null;

  const fetchVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getContentMetadata();
      setVideos(data || []);
      if (!Array.isArray(data)) {
        console.warn("getContentMetadata did not return an array:", data);
        setVideos([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load videos");
      console.error("Error fetching videos for modal:", err);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchVideos();
    }
  }, [open]);

  const handleVideoClick = (video) => {
    if (video && video.videoKey) {
      onSelectVideo(video.videoKey);
    }
    onClose();
  };

  const handlePlayVideo = (video) => {
    if (video && video.videoKey) {
      setPlayingVideoKey(video.videoKey);
      setPlayingVideoName(video.name);
      setPlayerOpen(true);
    }
  };

  const handlePlayYoutubePreview = () => {
    if (!normalizedYoutubeValue) return;
    setPlayingVideoKey(normalizedYoutubeValue);
    setPlayingVideoName(
      getYouTubeDisplayLabel(normalizedYoutubeValue, youtubeTitle)
    );
    setPlayerOpen(true);
  };

  const handleSelectYoutube = () => {
    const normalized = normalizeYouTubeVideo(youtubeUrl);
    if (!normalized) {
      setYoutubeError(
        "Enter a valid YouTube link (watch, Shorts, or youtu.be)."
      );
      return;
    }
    onSelectVideo(normalized);
    onClose();
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setPlayingVideoKey(null);
    setPlayingVideoName(null);
  };

  const filteredVideos = videos.filter((video) =>
    video.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  useEffect(() => {
    if (!open) {
      setFilterText("");
      setYoutubeUrl("");
      setYoutubeError("");
      setYoutubeTitle("");
      setTab(0);
    }
  }, [open]);

  useEffect(() => {
    if (!normalizedYoutubeValue) {
      setYoutubeTitle("");
      return;
    }

    let cancelled = false;
    fetchYouTubeVideoTitle(normalizedYoutubeValue).then((title) => {
      if (!cancelled) setYoutubeTitle(title || "");
    });

    return () => {
      cancelled = true;
    };
  }, [normalizedYoutubeValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Video</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mb: 2 }}
        >
          <Tab label="Content Library" />
          <Tab label="YouTube" />
        </Tabs>

        {tab === 0 && (
          <>
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                Error loading videos: {error}
              </Alert>
            )}
            {!isLoading && !error && (
              <>
                <TextField
                  fullWidth
                  label="Filter videos by name"
                  variant="outlined"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  sx={{ mb: 2 }}
                  autoFocus
                />
                <List>
                  {filteredVideos.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary={
                          filterText
                            ? `No videos found matching "${filterText}".`
                            : "No videos found in your library."
                        }
                      />
                    </ListItem>
                  )}
                  {filteredVideos.map((video) => (
                    <ListItem
                      key={video.contentId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        py: 2,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                      onClick={() => handleVideoClick(video)}
                    >
                      <Box
                        sx={{
                          width: 120,
                          height: 68,
                          backgroundColor: "grey.200",
                          borderRadius: 1,
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        {video.thumbnailKey && (
                          <img
                            src={`https://trainer-app-videos.s3.us-east-2.amazonaws.com/${video.thumbnailKey}`}
                            alt={video.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <ListItemText
                          primary={video.name}
                          secondary={`Tags: ${video.tags?.join(", ") || "None"}`}
                        />
                      </Box>

                      <IconButton
                        edge="end"
                        aria-label="preview"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayVideo(video);
                        }}
                      >
                        <PlayCircleOutlineIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}

        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Paste a YouTube video or Shorts link. It will be saved to the
              exercise and shown next to the exercise name in the app.
            </Typography>
            <TextField
              fullWidth
              label="YouTube URL"
              placeholder="https://www.youtube.com/watch?v=… or https://youtube.com/shorts/…"
              variant="outlined"
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.target.value);
                setYoutubeError("");
              }}
              error={Boolean(youtubeError)}
              helperText={
                youtubeError ||
                "Supports youtube.com/watch, youtu.be, and youtube.com/shorts links"
              }
              autoFocus
            />

            {parsedYoutubeId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 68,
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={getYouTubeThumbnailUrl(normalizedYoutubeValue)}
                    alt="YouTube preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {getYouTubeDisplayLabel(
                      normalizedYoutubeValue,
                      youtubeTitle
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {parsedYoutubeId}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="preview YouTube video"
                  onClick={handlePlayYoutubePreview}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!parsedYoutubeId}
                onClick={handleSelectYoutube}
              >
                Add YouTube Video
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <VideoPlayerModal
        open={playerOpen}
        onClose={handleClosePlayer}
        videoKey={playingVideoKey}
        videoName={playingVideoName}
      />
    </Dialog>
  );
}

export default VideoSelectionModal;
